import { ApiError, apiErrorCode, apiErrorMessage } from "./api.js";
import type { PaymentMethod } from "../../../shared/types.js";

/** Log API failures to the browser console for local debugging. */
export function logApiError(context: string, err: unknown): void {
  if (err instanceof ApiError) {
    console.error(`[zenpay] ${context}`, {
      status: err.status,
      message: err.message,
      error: apiErrorCode(err),
      detail: apiErrorMessage(err),
      body: err.body,
    });
    return;
  }
  console.error(`[zenpay] ${context}`, err);
}

export function checkoutErrorMessage(
  err: unknown,
  paymentMethod: PaymentMethod,
  t: (key: string) => string,
): string {
  if (err instanceof ApiError) {
    const code = apiErrorCode(err);
    if (code === "invalid_cpf") return t("checkout.errors.cpf");
    if (code === "invalid_phone") return t("checkout.errors.phone");
    if (code === "invalid_email") return t("checkout.errors.email");
    if (code === "invalid_full_name") return t("checkout.errors.fullName");
    if (code === "openpix_brazil_only") return t("checkout.pixBrazilOnly");
    if (code === "openpix_locale_unavailable") return t("checkout.pixLocaleUnavailable");
    if (code === "stripe_checkout_failed") return t("stripeCheckout.checkoutFailed");
    if (code === "pix_unavailable") return t("checkout.pixUnavailable");
  }
  if (paymentMethod === "stripe") return t("stripeCheckout.checkoutFailed");
  return t("common.error");
}

/**
 * Woovi failed to create the PIX on its side (`pix_unavailable`, 15/10/2026): the screen says PIX
 * is unstable and offers the card, where there is one.
 */
export function isPixUnavailableError(err: unknown): boolean {
  return err instanceof ApiError && apiErrorCode(err) === "pix_unavailable";
}

/** Technical detail for dev UI / support — never show raw messages to customers in prod. */
export function checkoutErrorDetail(err: unknown): string | null {
  if (err instanceof ApiError) {
    return apiErrorMessage(err) ?? null;
  }
  if (err instanceof Error && err.message) return err.message;
  return null;
}

export function stripeCheckoutLoadErrorMessage(err: unknown, t: (key: string) => string): string {
  if (err instanceof ApiError) {
    const code = apiErrorCode(err);
    if (code === "missing_session" || code === "session_unavailable") {
      return t("stripeCheckout.sessionUnavailable");
    }
    if (code === "stripe_session_retrieve_failed" || code === "stripe_checkout_failed") {
      return t("stripeCheckout.loadFailed");
    }
    if (code === "not_stripe" || code === "not_pending" || code === "not_found") {
      return t("stripeCheckout.loadFailed");
    }
  }
  return t("stripeCheckout.loadFailed");
}

export function stripeCheckoutLoadErrorDetail(err: unknown): string | null {
  return checkoutErrorDetail(err);
}
